import { useCallback } from 'react';
import Stack from '@mui/material/Stack';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader, Dialog, Floating } from 'components';
import CollapsibleTable from 'components/atoms/GefMui/CollapsibleTable';
import PaginationRow from 'components/atoms/GefMui/PaginationRow';
import SortCell from 'components/atoms/GefMui/SortCell';
import TableHasNoResults from 'components/atoms/GefMui/TableHasNoResults';
import collapsibleTable from 'features/common/collapsibleTable';
import fetch from 'features/common/fetch';
import { subheaders } from 'features/common/Subheader/constants';
import { getVisibleStateById } from 'features/common/Subheader/selectors';
import { loadMoreQuickFilter, loadPageQuickFilter, reloadLeads, sortLeads } from 'features/views/marketPlace/actions';
import { dialogs, quickFilter } from 'features/views/marketPlace/constants';
import { getMarketPlaceLeads, getQuickFilterState, getSortValue } from 'features/views/marketPlace/selectors';

import MarketPlaceTableRow from './MarketPlaceTableRow';
import NextSevenDaysPills from './NextSevenDaysPills';

const TableQuickFilters = () => {
	const dispatch = useDispatch();
	const records = useSelector(getMarketPlaceLeads);
	const sortValue = useSelector(getSortValue);
	const quickFilterState = useSelector(getQuickFilterState);
	const subheaderOpen = useSelector((state) => getVisibleStateById(state, subheaders.marketPlace, false));
	const isCurrentFilterOrSubheaderOpen = quickFilterState === quickFilter.CURRENT || subheaderOpen;

	const hasRecords = !isNilOrEmpty(records);

	const handleSort = useCallback(
		(sort) => {
			dispatch(collapsibleTable.actions.removeOpenedRows('MarketPlaceLeadsTable'));
			dispatch(sortLeads(sort));
		},
		[dispatch],
	);

	const handleReload = useCallback(() => {
		dispatch(reloadLeads(sortValue));
	}, [dispatch, sortValue]);

	const handleLoadMore = useCallback(() => {
		dispatch(loadMoreQuickFilter());
	}, [dispatch]);

	const handlePaginationClick = useCallback(
		(e, page) => {
			dispatch(loadPageQuickFilter(page - 1));
		},
		[dispatch],
	);

	return (
		<ComponentLoader fetchStorePath={fetch.constants.urls.leadsList} onReload={handleReload}>
			<Stack spacing={1}>
				{quickFilterState === quickFilter.FUTURE && !subheaderOpen && <NextSevenDaysPills />}
				<Floating>
					<Dialog mui name={dialogs.tooManyResults}>
						{getLabelMessage('marketPlace:tooManyResults')}
					</Dialog>
				</Floating>
				<TableContainer>
					<CollapsibleTable
						tableId="MarketPlaceLeadsTable"
						autoCloseOtherRows
						tableHeader={
							<TableHead>
								<TableRow>
									<TableCell />
									<TableCell>
										<SortCell
											hasRecords={hasRecords}
											sortBy="taskTypeName"
											sort={sortValue}
											onSort={handleSort}
										>
											{getLabelMessage('marketPlace:opportunityType')}
										</SortCell>
										<div>{getLabelMessage('marketPlace:opportunitySubtype')}</div>
									</TableCell>
									<TableCell />
									<TableCell>
										<div>{getLabelMessage('marketPlace:fullNameOrBussinessName')}</div>
									</TableCell>
									<TableCell>
										<div>{getLabelMessage('marketPlace:municipality')}</div>
										<div>{getLabelMessage('marketPlace:zipCode')}</div>
									</TableCell>
									<TableCell>
										<div>{getLabelMessage('marketPlace:phone')}</div>
									</TableCell>
									<TableCell>
										<div>{getLabelMessage('marketPlace:email')}</div>
									</TableCell>
									{/* only checking the SLA icon - because expiration is handled by cond below */}
									{isCurrentFilterOrSubheaderOpen && <TableCell />}
									{/* expiration column is on these quickfilters - current, inProgress, subheaderOpen */}
									{(quickFilterState !== quickFilter.FUTURE || subheaderOpen) && (
										<TableCell>{getLabelMessage('marketPlace:expiration')}</TableCell>
									)}
									<TableCell />
									<TableCell />
									<TableCell />
								</TableRow>
							</TableHead>
						}
						tableBody={
							!hasRecords ? (
								<TableHasNoResults />
							) : (
								records.map((record, id) => <MarketPlaceTableRow record={record} id={id} />)
							)
						}
					>
						<PaginationRow
							fetchStorePath={fetch.constants.urls.leadsList}
							colSpan={100}
							onLoadNext={handleLoadMore}
							onPaginationClick={handlePaginationClick}
						/>
					</CollapsibleTable>
				</TableContainer>
			</Stack>
		</ComponentLoader>
	);
};

export default TableQuickFilters;
